import { useEffect, useRef, useState } from "react";
import { getSheetSize } from "@plaatplan/core";
import { resolveCoreMessage } from "@plaatplan/i18n";
import { computePlannerBoardFitScale } from "../board/computePlannerBoardScale";
import { useLocale } from "../i18n/useLocale";
import { BoardAccessibleSummary } from "./BoardAccessibleSummary";

function rgba(hex, alpha) {
  const normalized = String(hex || "#37782d").replace("#", "");
  if (normalized.length !== 6) return `rgba(55,120,45,${alpha})`;
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function BoardView({
  sourcePanel,
  sheet,
  visible,
  tabId,
  panelId,
  summaryId,
}) {
  const stageRef = useRef(null);
  const [scale, setScale] = useState(0.3);
  const { display } = useLocale();
  const { length, width } = getSheetSize(sheet, sourcePanel);
  const restStockLabel = resolveCoreMessage(display, sheet.restStockLabel);
  const unusedMessage = resolveCoreMessage(display, sheet.unusedMessage);

  useEffect(() => {
    if (!visible) return undefined;
    const updateScale = () => {
      const stageWidth = stageRef.current?.clientWidth || 820;
      setScale(computePlannerBoardFitScale(stageWidth, length));
    };
    updateScale();
    const observer = new ResizeObserver(updateScale);
    if (stageRef.current) observer.observe(stageRef.current);
    return () => observer.disconnect();
  }, [length, visible]);

  return (
    <div
      className={`board-panel ${visible ? "active" : ""}`}
      role="tabpanel"
      id={panelId}
      aria-labelledby={tabId}
      aria-hidden={!visible}
    >
      <div className="visual-subheading">
        <strong>
          {sheet.unused
            ? display.t("web.board.unusedTitle")
            : display.t("web.board.sheetTitle", { number: sheet.index + 1 })}
        </strong>
        {sheet.source === "rest" && (
          <span className="sheet-source-badge">{display.t("web.board.restBadge")}</span>
        )}
        {sheet.unused && (
          <span className="sheet-source-badge sheet-source-badge--unused">
            {display.t("web.board.unusedBadge")}
          </span>
        )}
        {!sheet.unused && (
          <span>
            {display.t("web.board.sheetMeta", {
              thickness: display.formatThickness(sheet.thickness),
              index: sheet.thicknessSheetIndex + 1,
            })}
          </span>
        )}
        {restStockLabel && <span>{restStockLabel}</span>}
        {sheet.unused && unusedMessage && <span>{unusedMessage}</span>}
      </div>
      <div className="axis horizontal">
        <span>{display.t("web.board.longAxis", { size: display.formatLength(length) })}</span>
      </div>
      <div className="board-with-axis">
        <div className="axis vertical">
          <span>{display.t("web.board.shortAxis", { size: display.formatLength(width) })}</span>
        </div>
        <div
          ref={stageRef}
          aria-describedby={summaryId}
          className="board-stage"
          style={{ height: `${width * scale + 34}px` }}
        >
          <div
            className={[
              "board",
              sheet.source === "rest" ? "board--rest" : "",
              sheet.unused ? "board--unused-rest" : "",
            ].filter(Boolean).join(" ")}
            style={{ width: `${length * scale}px`, height: `${width * scale}px` }}
          >
            {sheet.unused && (
              <div className="board-unused-overlay" role="note">
                <p>{unusedMessage}</p>
              </div>
            )}
            {sheet.placements.map((placement) => (
              <div
                className="placed-panel"
                key={`${placement.panelId}-${placement.occurrence}`}
                style={{
                  left: `${placement.x * scale}px`,
                  top: `${placement.y * scale}px`,
                  width: `${placement.length * scale}px`,
                  height: `${placement.width * scale}px`,
                  borderColor: placement.projectColor,
                  background: rgba(placement.projectColor, 0.18),
                }}
                title={`${placement.projectName}, ${placement.name}: ${display.formatPanelSize(placement.originalLength, placement.originalWidth)} × ${display.formatThickness(placement.thickness)}`}
              >
                <div className="piece-title">
                  <strong>{placement.name}</strong>
                </div>
                <div className="piece-project" style={{ color: placement.projectColor }}>
                  {placement.projectName}
                </div>
                <div className="piece-dimensions">
                  {display.formatPanelSize(placement.originalLength, placement.originalWidth)}
                  {" × "}
                  {display.formatThickness(placement.thickness)}
                  {placement.rotated ? " ↻" : ""}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <BoardAccessibleSummary id={summaryId} sheet={sheet} />
    </div>
  );
}
